import { useState } from 'react';
import { equals } from 'ramda';
import { useFormik } from 'formik';
import { DateTimePicker } from '@mui/x-date-pickers';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ConfirmationDialog,
  DATE_FORMAT,
  ExamFormValues,
  PROCTORING_TYPE,
  TIMEZONE_MESSAGE,
} from '@shared';
import { examValidationSchema } from './schema';

type PropTypes = {
  courseName: string;
  safeBrowserEnabled: boolean;
  initialValues: ExamFormValues;
  isLoading: boolean;
  onSubmit: (values: ExamFormValues) => void;
  onExit: () => void;
  title: string;
  submitButtonLabel: string;
};

const checkboxes = [
  { label: 'Disable Navigation', value: 'disableNavigation' },
  { label: 'Disable Print Screen', value: 'disablePrintScreen' },
  { label: 'Disable Copy & Paste', value: 'disableCopyPaste' },
  { label: 'Force Full Screen Browser', value: 'forceFullScreen' },
  { label: 'Enable Whitelisted URLs', value: 'enableWhitelistedUrls' },
];

export const Form = ({
  initialValues,
  courseName,
  safeBrowserEnabled,
  isLoading,
  onSubmit,
  onExit,
  title,
  submitButtonLabel,
}: PropTypes) => {
  const [isConfirmationOpen, setConfirmationOpen] = useState<boolean>(false);

  const toggleConfirmationOpen = () => setConfirmationOpen((prev) => !prev);

  const handleApproveConfirm = () => {
    toggleConfirmationOpen();
    onExit();
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    touched,
    isValid,
    errors,
  } = useFormik({
    initialValues,
    validationSchema: examValidationSchema,
    validateOnBlur: true,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  const { enabled } = values.secureBrowserSettings;
  const isSecureBrowserOnlyNoProctoring =
    String(values.proctoringOption) === 'SECURE_BROWSER_ONLY_NO_PROCTORING';
  const isUnsavedChanges = equals(values, initialValues);

  const handleChangeSecureBrowser = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFieldValue(name, checked);
    if (name === 'secureBrowserSettings.enabled') {
      if (!checked) {
        setFieldValue('secureBrowserSettings.disableNavigation', false);
        setFieldValue('secureBrowserSettings.disablePrintScreen', false);
        setFieldValue('secureBrowserSettings.disableCopyPaste', false);
        setFieldValue('secureBrowserSettings.forceFullScreen', false);
        setFieldValue('secureBrowserSettings.enableWhitelistedUrls', false);
      }
    } else {
      if (name === 'secureBrowserSettings.disableNavigation' && checked) {
        setFieldValue('secureBrowserSettings.enableWhitelistedUrls', false);
      }
      if (name === 'secureBrowserSettings.enableWhitelistedUrls' && checked) {
        setFieldValue('secureBrowserSettings.disableNavigation', false);
      }
    }
  };

  const handleChangeProctoringOption = (event: SelectChangeEvent) => {
    const selectedOption = event.target.value;
    setFieldValue('proctoringOption', selectedOption);
    if (selectedOption === 'SECURE_BROWSER_ONLY_NO_PROCTORING') {
      setFieldValue('secureBrowserSettings.enabled', true);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Typography variant='h6' mb={5}>
        {title}
      </Typography>
      <Stack spacing={2} alignItems='stretch' mb={2}>
        <TextField
          fullWidth
          required
          name='name'
          label='Exam Name'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.name}
          error={touched.name && Boolean(errors.name)}
          helperText={touched.name && errors.name}
        />
        <TextField fullWidth name='name' label='Course Name' value={courseName} disabled />
        <TextField
          fullWidth
          required
          name='url'
          label='Exam URL'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.url}
          error={touched.url && Boolean(errors.url)}
          helperText={touched.url && errors.url}
        />
        <TextField
          fullWidth
          name='password'
          label='Password'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.password}
          error={touched.password && Boolean(errors.password)}
          helperText={touched.password && errors.password}
        />
        <FormControl required>
          <InputLabel id='test-select-label'>Proctoring Type</InputLabel>
          <Select
            label='Proctoring Type'
            name='proctoringOption'
            value={values.proctoringOption}
            onChange={handleChangeProctoringOption}
          >
            {Object.keys(PROCTORING_TYPE).map((key, index) => (
              <MenuItem key={index} value={key}>
                {PROCTORING_TYPE[key as keyof typeof PROCTORING_TYPE]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <DateTimePicker
          name='start'
          label='Start Date (optional)'
          onChange={(value) => setFieldValue('start', value)}
          value={values.start}
          format={DATE_FORMAT}
          ampm={false}
          slotProps={{
            textField: {
              onBlur: handleBlur,
              fullWidth: true,
              variant: 'outlined',
              helperText: (touched.start && errors.start) || (values.start && TIMEZONE_MESSAGE),
              error: touched.start && Boolean(errors.start),
            },
          }}
        />
        <DateTimePicker
          name='end'
          label='End Date'
          onChange={(value) => setFieldValue('end', value)}
          value={values.end}
          format={DATE_FORMAT}
          ampm={false}
          slotProps={{
            textField: {
              required: true,
              fullWidth: true,
              onBlur: handleBlur,
              variant: 'outlined',
              helperText: (touched.end && errors.end) || (values.end && TIMEZONE_MESSAGE),
              error: touched.end && Boolean(errors.end),
            },
          }}
        />
        {safeBrowserEnabled && (
          <FormGroup>
            <FormControlLabel
              label='Secure Browser Required'
              control={
                <Checkbox
                  name='secureBrowserSettings.enabled'
                  checked={enabled || isSecureBrowserOnlyNoProctoring}
                  onChange={handleChangeSecureBrowser}
                  disabled={isSecureBrowserOnlyNoProctoring}
                />
              }
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
              {checkboxes.map(({ value, label }) => (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      name={`secureBrowserSettings.${value}`}
                      checked={
                        values.secureBrowserSettings[
                          value as keyof typeof values.secureBrowserSettings
                        ] as boolean
                      }
                      onChange={handleChangeSecureBrowser}
                    />
                  }
                  label={`Secure Browser: ${label}`}
                  disabled={!enabled}
                />
              ))}
              <TextField
                fullWidth
                multiline
                rows={5}
                minRows={5}
                maxRows={10}
                name='secureBrowserSettings.whitelistedUrls'
                label='Whitelisted URLs (one per line)'
                onChange={handleChange}
                disabled={!values.secureBrowserSettings.enableWhitelistedUrls}
                error={
                  touched.secureBrowserSettings?.whitelistedUrls &&
                  Boolean(errors.secureBrowserSettings?.whitelistedUrls)
                }
                helperText={
                  touched.secureBrowserSettings?.whitelistedUrls &&
                  errors.secureBrowserSettings?.whitelistedUrls
                }
                onBlur={handleBlur}
                value={values.secureBrowserSettings.whitelistedUrls}
              />
            </Box>
          </FormGroup>
        )}
      </Stack>
      <Stack direction='row' spacing={1}>
        <Button onClick={!isUnsavedChanges ? toggleConfirmationOpen : onExit} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant='contained' disabled={!isValid} type='submit'>
          {isLoading ? <CircularProgress size={18} color='inherit' /> : submitButtonLabel}
        </Button>
      </Stack>
      <ConfirmationDialog
        isConfirmationOpen={isConfirmationOpen}
        title='Unsaved changes'
        text='This page contains unsaved changes. Do you still wish to leave the page?'
        labelButton='Leave'
        toggleConfirmationOpen={toggleConfirmationOpen}
        handleApproveConfirm={handleApproveConfirm}
      />
    </form>
  );
};
